import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Currency = 'USD' | 'AUD';

interface CurrencyContextType {
  currency: Currency;
  setCurrency: (currency: Currency) => void;
  formatPrice: (usdPrice: number, audPrice: number) => string;
  getPrice: (usdPrice: number, audPrice: number) => number;
  currencySymbol: string;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

interface CurrencyProviderProps {
  children: ReactNode;
}

export const CurrencyProvider: React.FC<CurrencyProviderProps> = ({ children }) => {
  const [currency, setCurrencyState] = useState<Currency>('AUD');

  // Load saved currency preference
  useEffect(() => {
    const savedCurrency = localStorage.getItem('preferredCurrency') as Currency;
    if (savedCurrency && (savedCurrency === 'USD' || savedCurrency === 'AUD')) {
      setCurrencyState(savedCurrency);
    }
  }, []);

  const setCurrency = (newCurrency: Currency) => {
    setCurrencyState(newCurrency);
    localStorage.setItem('preferredCurrency', newCurrency);
  };

  const formatPrice = (usdPrice: number, audPrice: number): string => {
    const price = currency === 'USD' ? usdPrice : audPrice;
    const symbol = currency === 'USD' ? '$' : 'A$';
    return `${symbol}${price.toLocaleString()}`;
  };

  const getPrice = (usdPrice: number, audPrice: number): number => {
    return currency === 'USD' ? usdPrice : audPrice;
  };

  const currencySymbol = currency === 'USD' ? '$' : 'A$';

  const value: CurrencyContextType = {
    currency,
    setCurrency,
    formatPrice,
    getPrice,
    currencySymbol
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};
